import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography, Badge } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { formatDispositioned } from 'utils/dispositionedDisplay';
import {
  BOOLEAN_YES_NO_OPTIONS,
  formatBooleanYesNo,
  formatStudentHousingPropertyType,
} from 'utils/formatBooleanYesNo';
import styled from 'styled-components';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { TableEmptyState } from 'components/common/TableEmptyState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import {
  PROPERTIES_TABLE_DEFAULT_VISIBLE_KEYS,
  PROPERTIES_TABLE_STORAGE_KEY,
} from './buildingTableConfig';

const LEAP_PURPLE = '#702572';

const buildBuildingTableRow = (building, isDemo) => ({
  name: isDemo ? getDemoData('building-name') : building.name,
  student_housing: building.student_housing,
  phone: isDemo ? getDemoData('phone') : building.phone,
  billingStreet: isDemo ? getDemoData('address') : building.billingStreet,
  email_address: isDemo ? getDemoData('email') : building.email_address,
  total_of_units: parseInt(building.total_of_units, 10) || 0,
  dispositioned: formatDispositioned(building.dispositioned),
  actions: building.building_id,
});

export const BuildingTable = forwardRef(
  (
    {
      buildings,
      filter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      dropFilter,
      onClickBuilding,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      onLoadFullDatasetTotal,
      isLoadingFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      showEmptyState,
      emptyContent,
      loading = false,
    },
    ref,
  ) => {
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    const tableData = useMemo(() => {
      if (!buildings) return [];
      return buildings.map((building) => buildBuildingTableRow(building, isDemo));
    }, [buildings, isDemo]);

    const goDetailPage = useCallback(
      (buildingId) => {
        const selectedBuilding = (buildings || []).find(
          (building) => building.building_id === buildingId,
        );
        if (selectedBuilding) onClickBuilding(selectedBuilding);
      },
      [buildings, onClickBuilding],
    );

    const columns = useMemo(
      () => [
        {
          name: 'Name',
          key: 'name',
          options: {
            minWidth: 160,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Property Type',
          key: 'student_housing',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 108, maxWidth: 148 }),
            sort: true,
            exportValue: (value) => formatStudentHousingPropertyType(value),
            customBodyRenderer: (value) => {
              const label = formatStudentHousingPropertyType(value);
              const isStudent = label === 'student';
              return (
                <Tooltip title={label} placement="top" showCarot>
                  <div>
                    <Badge
                      background="#F3F1F4"
                      color={isStudent ? 'parisGreen' : 'tomato'}
                      label={label}
                      rounded
                      textSize="medium"
                    />
                  </div>
                </Tooltip>
              );
            },
            filterOptions: BOOLEAN_YES_NO_OPTIONS,
          },
        },
        {
          name: 'Phone Number',
          key: 'phone',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 180 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Address',
          key: 'billingStreet',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 360 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Email Address',
          key: 'email_address',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 320 }),
            sort: true,
            customBodyRenderer: (value) => (
              <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
                {value}
              </CustomTypography>
            ),
          },
        },
        {
          name: 'Unit Count',
          key: 'total_of_units',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 120, align: 'center' }),
            filter: false,
            sort: true,
            customBodyRenderer: (value) => (
              <Typography align="center" variant="body1">
                {value.toLocaleString('en-US')}
              </Typography>
            ),
          },
        },
        {
          name: 'Dispositioned',
          key: 'dispositioned',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 108, maxWidth: 132 }),
            sort: true,
            exportValue: (value) => formatBooleanYesNo(formatDispositioned(value)),
            customBodyRenderer: (value) => {
              const normalizedValue = formatDispositioned(value);
              const label = formatBooleanYesNo(normalizedValue);
              return (
                <Tooltip title={label} placement="top" showCarot>
                  <div>
                    <Badge
                      background="#F3F1F4"
                      color={normalizedValue === 'true' ? 'tomato' : 'parisGreen'}
                      label={label}
                      rounded
                      textSize="medium"
                    />
                  </div>
                </Tooltip>
              );
            },
          },
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 64,
            minWidth: 64,
            maxWidth: 64,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (value) => (
              <ActionsCell onClick={(e) => e.stopPropagation()}>
                <Tooltip title="View property details" placement="top" showCarot>
                  <span>
                    <ActionIconButton
                      type="button"
                      aria-label="View property details"
                      onClick={() => goDetailPage(value)}
                    >
                      <IconGraphy icon="FileFolder.Description" style={{ color: LEAP_PURPLE }} />
                    </ActionIconButton>
                  </span>
                </Tooltip>
              </ActionsCell>
            ),
          },
        },
      ],
      [goDetailPage],
    );

    return (
      <>
        <Table
          loading={loading}
          showEmptyState={showEmptyState}
          emptyContent={
            emptyContent ?? (
              <TableEmptyState
                entityName="properties"
                hasActiveFilters={Boolean(`${filter?.searchText || ''}`.trim())}
              />
            )
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          data={tableData}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={PROPERTIES_TABLE_DEFAULT_VISIBLE_KEYS}
          columnVisibilityStorageKey={PROPERTIES_TABLE_STORAGE_KEY}
          columnWidthStorageKey={PROPERTIES_TABLE_STORAGE_KEY}
          onRequestExportData={async () => {
            const exportRows = onRequestExportData ? await onRequestExportData() : buildings;
            return (exportRows || []).map((building) => buildBuildingTableRow(building, isDemo));
          }}
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const exportRows = await onRequestFullExportData();
                  return (exportRows || []).map((building) =>
                    buildBuildingTableRow(building, isDemo),
                  );
                }
              : undefined
          }
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          leastColumnCount={1}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title="Properties"
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title="Properties"
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          onLoadFullDatasetTotal={onLoadFullDatasetTotal}
          isLoadingFullDatasetTotal={isLoadingFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

BuildingTable.displayName = 'BuildingTable';

BuildingTable.defaultProps = {
  showEmptyState: true,
  emptyContent: null,
};

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ActionIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;
  cursor: pointer;
`;
